// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { QRCodeCanvas } from 'qrcode.react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Printer, CheckCircle2 } from 'lucide-react';
import { formatCurrency, formatDate, formatDateTime } from '@/utils/formatters';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  registrationId: string;
  teamName?: string;
}

function CheckinQrSection({ registrationId, teamName, eventName }: { registrationId: string; teamName?: string; eventName?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['registration-checkin-token', registrationId],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('rle-registration-checkin-token', {
        body: { event_registration_id: registrationId },
      });
      if (error) throw error;
      return data;
    },
  });

  const handlePrint = () => {
    const canvas = document.querySelector<HTMLCanvasElement>(`#checkin-qr-${registrationId} canvas`);
    const dataUrl = canvas ? canvas.toDataURL('image/png') : '';
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) return;
    w.document.write(`
      <html><head><title>Check-in QR</title>
      <style>body{font-family:Inter,system-ui,sans-serif;text-align:center;padding:40px;}h2{margin:0 0 4px;}p{color:#555;}</style>
      </head><body>
        <h2>${teamName ?? ''}</h2>
        <p>${eventName ?? ''}</p>
        ${dataUrl ? `<img src="${dataUrl}" width="280" height="280" />` : ''}
        <p>Show this code at event check-in.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="md:col-span-2">
      <Section title="Check-in QR">
        {isLoading ? (
          <Skeleton className="h-44 w-44" />
        ) : error ? (
          <p className="text-sm text-destructive">{(error as any).message}</p>
        ) : (
          <div className="flex flex-col items-start gap-3">
            {data?.data?.checked_in_at ? (
              <Badge className="gap-1 bg-green-100 text-green-800 hover:bg-green-100">
                <CheckCircle2 className="h-3.5 w-3.5" /> Checked in {formatDateTime(data.data.checked_in_at)}
              </Badge>
            ) : null}
            {data?.data?.checkin_url ? (
              <>
                <div id={`checkin-qr-${registrationId}`} className="rounded-md border bg-white p-3">
                  <QRCodeCanvas value={data.data.checkin_url} size={176} includeMargin />
                </div>
                <div className="text-sm">
                  <div className="font-medium">{teamName}{eventName ? ` · ${eventName}` : ''}</div>
                  <div className="text-muted-foreground">Show this code at event check-in.</div>
                </div>
                <Button size="sm" variant="outline" onClick={handlePrint} className="gap-1.5">
                  <Printer className="h-4 w-4" /> Print
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No check-in code available.</p>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

const money = (v: any) => formatCurrency(Number(v ?? 0));
const has = (v: any) =>
  v !== null && v !== undefined && v !== '' && !(typeof v === 'number' && v === 0);
const hasAny = (...vals: any[]) => vals.some((v) => has(v));

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (!has(value)) return null;
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

const PARAM_LABELS: Record<string, string> = {
  blackout_date: 'Date',
  start_time: 'Start',
  end_time: 'End',
  date: 'Date',
  time: 'Time',
  related_team_id: 'Related team',
};

function renderParams(params: Record<string, any>) {
  if (!params || typeof params !== 'object') return null;
  const entries = Object.entries(params).filter(([, v]) => has(v));
  if (entries.length === 0) return null;
  // Special-case time ranges
  if (has(params.start_time) && has(params.end_time)) {
    const rest = entries.filter(([k]) => k !== 'start_time' && k !== 'end_time');
    return (
      <div className="text-xs text-muted-foreground">
        {rest.map(([k, v]) => `${PARAM_LABELS[k] ?? k}: ${v}`).join(' · ')}
        {rest.length > 0 ? ' · ' : ''}
        {params.start_time}–{params.end_time}
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground">
      {entries.map(([k, v]) => `${PARAM_LABELS[k] ?? k}: ${v}`).join(' · ')}
    </div>
  );
}

export default function RegistrationDetailPanel({ registrationId, teamName }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['registration-detail', registrationId],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('rle-registration-detail', {
        body: { event_registration_id: registrationId },
      });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <p className="text-sm text-destructive">{(error as any).message}</p>;

  const d = data?.data;
  if (!d) return <p className="text-sm text-muted-foreground">No details available.</p>;

  const event = d.event ?? {};
  const payment = d.payment ?? {};
  const scheduling = d.scheduling ?? {};
  const roster = d.roster ?? {};
  const checkIn = d.check_in ?? {};
  const comp = d.comp ?? {};
  const extras = d.extras ?? [];
  const constraints = scheduling.constraints ?? [];
  const activeLink = (d.payment_links ?? []).find((l: any) => l.status === 'active');
  const customFields = d.custom_field_responses ?? {};
  const customEntries = Object.entries(customFields).filter(([, v]) => has(v));

  return (
    <div className="grid gap-6 rounded-md border bg-muted/30 p-4 md:grid-cols-2">
      <CheckinQrSection registrationId={registrationId} teamName={teamName} eventName={event.name} />

      {/* Event & division */}
      {hasAny(event.name, event.start_date, event.venue_name, d.division, d.age_group, d.pool) && (
        <Section title="Event & division">
          <Row label="Event" value={event.name} />
          {has(event.start_date) && (
            <Row
              label="Dates"
              value={`${formatDate(event.start_date)}${event.end_date ? ` – ${formatDate(event.end_date)}` : ''}`}
            />
          )}
          <Row label="Venue" value={event.venue_name} />
          <Row label="Division" value={d.division} />
          <Row label="Age group" value={d.age_group} />
          <Row label="Pool" value={d.pool} />
        </Section>
      )}

      {/* Payment */}
      {hasAny(
        payment.status,
        payment.method_chosen,
        d.total_amount_due,
        d.total_amount_paid,
        d.balance_due,
        d.total_amount_refunded,
        payment.due_at,
        d.registered_at,
        activeLink,
      ) && (
        <Section title="Payment">
          <Row label="Status" value={payment.status} />
          <Row label="Method" value={payment.method_chosen} />
          <Row label="Total due" value={has(d.total_amount_due) ? money(d.total_amount_due) : null} />
          <Row label="Paid" value={has(d.total_amount_paid) ? money(d.total_amount_paid) : null} />
          <Row label="Balance due" value={has(d.balance_due) ? money(d.balance_due) : null} />
          <Row label="Refunded" value={has(d.total_amount_refunded) ? money(d.total_amount_refunded) : null} />
          <Row label="Due at" value={has(payment.due_at) ? formatDateTime(payment.due_at) : null} />
          {/* Offline payment */}
          <Row label="Received at" value={has(payment.received_at) ? formatDateTime(payment.received_at) : null} />
          <Row label="Received via" value={payment.received_method} />
          <Row label="Reference" value={payment.received_reference} />
          <Row label="Received amount" value={has(payment.received_amount) ? money(payment.received_amount) : null} />
          <Row label="Late fee charged" value={has(payment.late_fee_charged) ? money(payment.late_fee_charged) : null} />
          <Row label="Late fee received" value={has(payment.late_fee_received) ? money(payment.late_fee_received) : null} />
          <Row label="Registered" value={has(d.registered_at) ? formatDateTime(d.registered_at) : null} />
          {activeLink && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Payment link</span>
              <a href={activeLink.url} target="_blank" rel="noreferrer" className="font-medium text-primary underline">
                Open link
              </a>
            </div>
          )}
        </Section>
      )}

      {/* Extras */}
      {extras.length > 0 && (
        <Section title="Extras">
          {extras.map((x: any, i: number) => (
            <div key={i} className="flex justify-between gap-4">
              <span className="text-muted-foreground">
                {x.name}
                {has(x.qty) ? ` × ${x.qty}` : ''}
              </span>
              <span className="font-medium">{has(x.price) ? money(x.price) : ''}</span>
            </div>
          ))}
          {has(d.extras_total) && (
            <div className="flex justify-between gap-4 border-t pt-1">
              <span className="font-medium">Extras total</span>
              <span className="font-medium">{money(d.extras_total)}</span>
            </div>
          )}
        </Section>
      )}

      {/* Scheduling */}
      {hasAny(scheduling.tier, scheduling.tier_fee, constraints.length, scheduling.constraints_total) && (
        <Section title="Scheduling">
          <Row label="Tier" value={scheduling.tier} />
          <Row label="Tier fee" value={has(scheduling.tier_fee) ? money(scheduling.tier_fee) : null} />
          {constraints.length > 0 && (
            <div className="space-y-2 pt-1">
              {constraints.map((c: any, i: number) => (
                <div key={i} className="rounded border bg-background p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{c.label}</span>
                    <span className="flex items-center gap-1.5">
                      {c.severity && (
                        <Badge variant="outline" className="text-[10px]">
                          {c.severity}
                        </Badge>
                      )}
                      {c.requires_review && (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px]">needs review</Badge>
                      )}
                      {has(c.fee) && <span className="text-sm font-medium">{money(c.fee)}</span>}
                    </span>
                  </div>
                  {renderParams(c.params)}
                </div>
              ))}
            </div>
          )}
          {has(scheduling.constraints_total) && (
            <div className="flex justify-between gap-4 border-t pt-1">
              <span className="font-medium">Constraints total</span>
              <span className="font-medium">{money(scheduling.constraints_total)}</span>
            </div>
          )}
        </Section>
      )}

      {/* Requests & notes */}
      {hasAny(d.special_requests, d.registrant_notes, customEntries.length) && (
        <Section title="Requests & notes">
          {has(d.special_requests) && (
            <Row
              label="Special requests"
              value={
                <>
                  {d.special_requests}
                  {has(d.special_requests_fee) ? ` (${money(d.special_requests_fee)})` : ''}
                </>
              }
            />
          )}
          <Row label="Notes" value={d.registrant_notes} />
          {customEntries.map(([k, v]) => (
            <Row key={k} label={k} value={String(v)} />
          ))}
        </Section>
      )}

      {/* Roster & check-in */}
      {hasAny(roster.size, roster.locked, checkIn.checked_in_at, comp.comped_at) && (
        <Section title="Roster & check-in">
          <Row label="Roster size" value={roster.size} />
          {has(roster.locked) && <Row label="Roster" value={roster.locked ? 'Locked' : 'Unlocked'} />}
          <Row label="Checked in" value={has(checkIn.checked_in_at) ? formatDateTime(checkIn.checked_in_at) : null} />
          <Row label="Comped at" value={has(comp.comped_at) ? formatDateTime(comp.comped_at) : null} />
          <Row label="Comp reason" value={comp.reason} />
        </Section>
      )}
    </div>
  );
}
